import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface PdfThumbnailNavigatorProps {
  thumbnails: Map<number, HTMLCanvasElement>;
  currentPage: number;
  onPageClick: (pageNumber: number) => void;
  isLoading: boolean;
  className?: string;
}

const getThumbnailCardStyles = (isCurrentPage: boolean) => {
  const baseStyles =
    'cursor-pointer rounded-lg border-2 p-2 w-full transition-all flex flex-col items-center';
  const activeStyles = 'border-primary shadow-md';
  const inactiveStyles =
    'border-transparent bg-white hover:border-gray-300 hover:bg-gray-100';

  if (isCurrentPage) {
    return `${baseStyles} ${activeStyles}`;
  }

  return `${baseStyles} ${inactiveStyles}`;
};

export const PdfThumbnailNavigator = ({
  thumbnails,
  currentPage,
  onPageClick,
  isLoading,
  className = '',
}: PdfThumbnailNavigatorProps) => {
  const currentThumbnailRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (currentThumbnailRef.current) {
      currentThumbnailRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentPage]);

  if (isLoading) {
    return (
      <div
        className={`bg-white/95 backdrop-blur-sm shadow-lg p-3 flex items-center justify-center ${className}`}
      >
        <div className="text-xs text-gray-500">{t('app.loading')}</div>
      </div>
    );
  }

  return (
    <div
      className={`bg-white/95 backdrop-blur-sm shadow-lg overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 ${className}`}
    >
      <div className="p-3 space-y-3">
        {Array.from(thumbnails.entries()).map(([pageNumber, thumbnail]) => (
          <button
            key={pageNumber}
            ref={pageNumber === currentPage ? currentThumbnailRef : null}
            aria-current={pageNumber === currentPage ? 'page' : undefined}
            className={getThumbnailCardStyles(pageNumber === currentPage)}
            onClick={() => onPageClick(pageNumber)}
          >
            <canvas
              ref={(canvas) => {
                if (canvas) {
                  const ctx = canvas.getContext('2d');
                  canvas.width = thumbnail.width;
                  canvas.height = thumbnail.height;
                  ctx?.drawImage(thumbnail, 0, 0);
                }
              }}
              className="w-full h-auto shadow-sm"
            />
            <div className="mt-2 text-xs text-center text-gray-600 font-medium">
              {pageNumber}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
